// src/edu-world/core/player-controller.ts
// Player movement: tap-to-move + arrow keys + mobile D-pad, boundary clamping (Phase 1)
// Uses real player.png sprite with scale adjustment.

import Phaser from 'phaser';
import type { PlayerConfig, ZoneConfig } from '../types';
import type { EventBus } from './event-bus';
import type { DpadDirection } from '../ui/mobile-dpad';

export class PlayerController {
  private scene: Phaser.Scene;
  private sprite: Phaser.GameObjects.Sprite;
  private config: PlayerConfig;
  private zone: ZoneConfig;
  private eventBus: EventBus;

  private targetX: number;
  private targetY: number;
  private isMoving: boolean = false;
  private inputLocked: boolean = false;

  // Keyboard arrow keys
  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private isKeyboardMoving: boolean = false;
  private wasMovingLastFrame: boolean = false;

  // Frame offsets from spritesheet editor
  private frameOffsets: { x: number; y: number }[] = [];

  // Collision barriers (elliptical zones the player cannot enter)
  // Loaded from zone config — editable via barrier editor mode (press B in-game)
  private barriers: { x: number; y: number; rx: number; ry: number }[] = [];

  // Polygon barriers (more precise — defined as arrays of vertices)
  private polygonBarriers: { x: number; y: number }[][] = [];

  // Tap interceptor — if set, called before movement. Return true to consume the tap.
  private tapInterceptor: ((worldX: number, worldY: number) => boolean) | null = null;

  // External D-pad direction (from mobile virtual D-pad)
  private externalDirection: DpadDirection = { x: 0, y: 0 };

  constructor(
    scene: Phaser.Scene,
    startX: number,
    startY: number,
    config: PlayerConfig,
    zone: ZoneConfig,
    eventBus: EventBus
  ) {
    this.scene = scene;
    this.config = config;
    this.zone = zone;
    this.eventBus = eventBus;
    this.targetX = startX;
    this.targetY = startY;

    // ── Player sprite (animated spritesheet) ──
    this.sprite = scene.add.sprite(startX, startY, 'player-walk', 0);
    this.sprite.setScale(0.08); // 313 * 0.08 = ~25px — proportional to the 1536px map
    this.sprite.setDepth(10);

    // ── Frame offsets (loaded from spritesheet editor via localStorage) ──
    // Corrects inconsistent character positioning between frames
    let frameOffsets: { x: number; y: number }[] = [];
    try {
      const saved = localStorage.getItem('spritesheet-offsets');
      if (saved) {
        frameOffsets = JSON.parse(saved);
        console.log('[PlayerController] Loaded spritesheet offsets:', frameOffsets.length, 'frames');
      }
    } catch {
      // No offsets saved — use defaults (no correction)
    }

    // Store offsets for use in stopWalkAnimation
    this.frameOffsets = frameOffsets;

    // Apply offset corrections on each animation frame change
    if (frameOffsets.length === 16) {
      // Apply idle frame offset immediately
      const idleOffset = frameOffsets[0];
      this.sprite.setOrigin(
        0.5 - (idleOffset.x / 313),
        0.5 - (idleOffset.y / 313)
      );

      this.sprite.on('animationupdate', () => {
        const frame = this.sprite.anims?.currentFrame;
        if (frame) {
          const frameIndex = typeof frame.textureFrame === 'number'
            ? frame.textureFrame
            : parseInt(String(frame.textureFrame), 10);
          if (!isNaN(frameIndex) && frameIndex >= 0 && frameIndex < frameOffsets.length) {
            const offset = frameOffsets[frameIndex];
            this.sprite.setOrigin(
              0.5 - (offset.x / 313),
              0.5 - (offset.y / 313)
            );
          }
        }
      });
    }

    // Create walk animations (4 rows × 4 columns in spritesheet)
    // Row 0 (frames 0-3): walk down
    // Row 1 (frames 4-7): walk left
    // Row 2 (frames 8-11): walk right
    // Row 3 (frames 12-15): walk up
    scene.anims.create({
      key: 'walk-down',
      frames: scene.anims.generateFrameNumbers('player-walk', { start: 0, end: 3 }),
      frameRate: 8,
      repeat: -1,
    });
    scene.anims.create({
      key: 'walk-left',
      frames: scene.anims.generateFrameNumbers('player-walk', { start: 4, end: 7 }),
      frameRate: 8,
      repeat: -1,
    });
    scene.anims.create({
      key: 'walk-right',
      frames: scene.anims.generateFrameNumbers('player-walk', { start: 8, end: 11 }),
      frameRate: 8,
      repeat: -1,
    });
    scene.anims.create({
      key: 'walk-up',
      frames: scene.anims.generateFrameNumbers('player-walk', { start: 12, end: 15 }),
      frameRate: 8,
      repeat: -1,
    });

    // ── Tap-to-move input ──
    scene.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (this.inputLocked) return;
      // Don't move if pointer hit an interactive UI element
      const hitObjects = scene.input.hitTestPointer(pointer);
      if (hitObjects.length > 0) return;
      // Allow tap interceptor (micro-interactions) to consume the tap
      if (this.tapInterceptor && this.tapInterceptor(pointer.worldX, pointer.worldY)) {
        return;
      }
      this.setTarget(pointer.worldX, pointer.worldY);
    });

    // ── Keyboard arrow keys ──
    if (scene.input.keyboard) {
      this.cursors = scene.input.keyboard.createCursorKeys();
    }
  }

  /** Lock/unlock player input (during dialogue, minigame, etc.) */
  setInputLocked(locked: boolean): void {
    this.inputLocked = locked;
  }

  /**
   * Set a tap interceptor callback. Called before movement on pointerdown.
   * If the callback returns true, the tap is consumed and player does not move.
   * Used by micro-interactions to intercept taps on interactive zones.
   */
  setTapInterceptor(interceptor: ((worldX: number, worldY: number) => boolean) | null): void {
    this.tapInterceptor = interceptor;
  }

  /** Set external direction input (from mobile D-pad) */
  setExternalDirection(direction: DpadDirection): void {
    this.externalDirection = direction;
  }

  /** Set collision barriers (loaded from config or barrier editor) */
  setBarriers(barriers: { x: number; y: number; rx: number; ry: number }[]): void {
    this.barriers = barriers;
  }

  /** Set polygon barriers (from barrier editor) */
  setPolygonBarriers(polygons: { x: number; y: number }[][]): void {
    this.polygonBarriers = polygons;
  }

  /** Set movement target, clamped to zone bounds */
  setTarget(x: number, y: number): void {
    const halfW = 20;
    const halfH = 30;

    this.targetX = Phaser.Math.Clamp(x, halfW, this.zone.width - halfW);
    this.targetY = Phaser.Math.Clamp(y, halfH, this.zone.height - halfH);
    this.isMoving = true;
  }

  update(): void {
    // ── Keyboard + D-pad movement ──
    if (!this.inputLocked) {
      let kdx = 0;
      let kdy = 0;

      if (this.cursors) {
        if (this.cursors.left.isDown) kdx = -1;
        else if (this.cursors.right.isDown) kdx = 1;
        if (this.cursors.up.isDown) kdy = -1;
        else if (this.cursors.down.isDown) kdy = 1;
      }

      // Merge with external D-pad direction
      if (this.externalDirection.x !== 0) kdx = this.externalDirection.x;
      if (this.externalDirection.y !== 0) kdy = this.externalDirection.y;

      if (kdx !== 0 || kdy !== 0) {
        this.isMoving = false;
        this.isKeyboardMoving = true;

        const speed = this.config.speed;
        const dt = this.scene.game.loop.delta / 1000;
        const moveDistance = speed * dt;

        const len = Math.sqrt(kdx * kdx + kdy * kdy);
        const newX = this.sprite.x + (kdx / len) * moveDistance;
        const newY = this.sprite.y + (kdy / len) * moveDistance;

        // Apply movement only if not blocked
        const clampedPos = this.clampPosition(newX, newY);
        this.sprite.x = clampedPos.x;
        this.sprite.y = clampedPos.y;

        // Play directional walk animation
        if (Math.abs(kdx) >= Math.abs(kdy)) {
          this.sprite.setFlipX(false);
          this.sprite.play(kdx < 0 ? 'walk-left' : 'walk-right', true);
        } else {
          this.sprite.setFlipX(false);
          this.sprite.play(kdy < 0 ? 'walk-up' : 'walk-down', true);
        }

        this.startWalkAnimation();
        return;
      } else {
        if (this.isKeyboardMoving) {
          this.stopWalkAnimation();
        }
        this.isKeyboardMoving = false;
      }
    }

    // ── Tap-to-move ──
    if (!this.isMoving) {
      // If we were moving last frame but stopped, stop animation
      if (this.wasMovingLastFrame) {
        this.stopWalkAnimation();
        this.wasMovingLastFrame = false;
      }
      return;
    }

    // We're moving
    this.wasMovingLastFrame = true;
    this.startWalkAnimation();

    const dx = this.targetX - this.sprite.x;
    const dy = this.targetY - this.sprite.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance < 4) {
      this.sprite.x = this.targetX;
      this.sprite.y = this.targetY;
      this.isMoving = false;
      return;
    }

    const speed = this.config.speed;
    const dt = this.scene.game.loop.delta / 1000;
    const moveDistance = speed * dt;

    let newX: number, newY: number;
    if (moveDistance >= distance) {
      newX = this.targetX;
      newY = this.targetY;
      this.isMoving = false;
    } else {
      const ratio = moveDistance / distance;
      newX = this.sprite.x + dx * ratio;
      newY = this.sprite.y + dy * ratio;
    }

    // Apply movement only if not blocked
    const clampedPos = this.clampPosition(newX, newY);
    this.sprite.x = clampedPos.x;
    this.sprite.y = clampedPos.y;

    // Play directional walk animation based on movement
    const moveDirX = this.targetX - this.sprite.x;
    const moveDirY = this.targetY - this.sprite.y;
    if (Math.abs(moveDirX) >= Math.abs(moveDirY)) {
      this.sprite.setFlipX(false);
      this.sprite.play(moveDirX < 0 ? 'walk-left' : 'walk-right', true);
    } else {
      this.sprite.setFlipX(false);
      this.sprite.play(moveDirY < 0 ? 'walk-up' : 'walk-down', true);
    }

    // If we hit a barrier and can't reach target, stop
    if (this.isMoving && this.isInsideBarrier(this.targetX, this.targetY)) {
      this.isMoving = false;
    }
  }

  /** Clamp position to world bounds AND check collision barriers */
  private clampPosition(x: number, y: number): { x: number; y: number } {
    const halfW = 20;
    const halfH = 30;

    // World bounds
    x = Phaser.Math.Clamp(x, halfW, this.zone.width - halfW);
    y = Phaser.Math.Clamp(y, halfH, this.zone.height - halfH);

    // Check ellipse barriers
    for (const barrier of this.barriers) {
      if (this.pointInEllipse(x, y, barrier)) {
        if (!this.pointInEllipse(this.sprite.x, y, barrier)) {
          x = this.sprite.x;
        } else if (!this.pointInEllipse(x, this.sprite.y, barrier)) {
          y = this.sprite.y;
        } else {
          x = this.sprite.x;
          y = this.sprite.y;
        }
      }
    }

    // Check polygon barriers
    for (const poly of this.polygonBarriers) {
      if (this.pointInPolygon(x, y, poly)) {
        // Try sliding on each axis
        if (!this.pointInPolygon(this.sprite.x, y, poly)) {
          x = this.sprite.x;
        } else if (!this.pointInPolygon(x, this.sprite.y, poly)) {
          y = this.sprite.y;
        } else {
          x = this.sprite.x;
          y = this.sprite.y;
        }
      }
    }

    return { x, y };
  }

  /** Check if a point is inside an elliptical barrier */
  private pointInEllipse(
    px: number, py: number,
    barrier: { x: number; y: number; rx: number; ry: number }
  ): boolean {
    const dx = (px - barrier.x) / barrier.rx;
    const dy = (py - barrier.y) / barrier.ry;
    return (dx * dx + dy * dy) <= 1;
  }

  /** Check if a target point is inside any barrier */
  private isInsideBarrier(x: number, y: number): boolean {
    if (this.barriers.some(b => this.pointInEllipse(x, y, b))) return true;
    if (this.polygonBarriers.some(p => this.pointInPolygon(x, y, p))) return true;
    return false;
  }

  /** Ray-casting point-in-polygon test */
  private pointInPolygon(px: number, py: number, polygon: { x: number; y: number }[]): boolean {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].x, yi = polygon[i].y;
      const xj = polygon[j].x, yj = polygon[j].y;

      const intersect = ((yi > py) !== (yj > py))
        && (px < (xj - xi) * (py - yi) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }
    return inside;
  }

  getSprite(): Phaser.GameObjects.Sprite {
    return this.sprite;
  }

  getPosition(): { x: number; y: number } {
    return { x: this.sprite.x, y: this.sprite.y };
  }

  getIsMoving(): boolean {
    return this.isMoving || this.isKeyboardMoving;
  }

  /** Start walk animation (now handled by sprite.play in update) */
  private startWalkAnimation(): void {
    // Animation is handled inline by sprite.play() calls
  }

  /** Stop walk animation and show idle frame */
  private stopWalkAnimation(): void {
    this.sprite.stop();
    this.sprite.setFrame(0); // idle = first frame (facing down)
    this.sprite.setFlipX(false);

    // Apply offset for idle frame (frame 0) to prevent jump on stop
    if (this.frameOffsets.length > 0) {
      const idleOffset = this.frameOffsets[0];
      this.sprite.setOrigin(
        0.5 - (idleOffset.x / 313),
        0.5 - (idleOffset.y / 313)
      );
    }
  }
}
